import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';

import { LOCATION_API_REFRESH_INTERVAL } from 'core/constants';
import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import { ActionEvent, DeletedUsersPayload } from 'utils/events/types';
import Logger from 'utils/logger';
import { setInstantInterval } from 'utils/time';
import LocationDetails from 'utils/stores/LocationDetails';

import { IncidentStatus, IncidentType } from 'utils/api/modules/incidents/incidents.types';
import { AlarmResponse, AmberAlarmResponse } from 'utils/api/modules/alarms/alarms.types';

import RootStore from '../Root';
import ActiveUser from './ActiveUser';
import { ActivityMapQueryParams } from './types';

export default class ActivityMapStore {
  store: RootStore;

  api: typeof API;

  @observable isReady = false;

  @observable bounds = '';

  @observable users: ActiveUser[] = [];

  @observable alarms: (AlarmResponse | AmberAlarmResponse)[] = [];

  @observable searchQuery = '';

  refreshMapInterval?: number;

  locationDetails: LocationDetails;

  constructor(rootStore: RootStore, api: typeof API) {
    makeObservable(this);
    this.store = rootStore;
    this.api = api;
    this.locationDetails = new LocationDetails(rootStore, api);

    eventBus.register(this);
  }

  @action.bound
  async refreshActiveUsers(): Promise<void> {
    try {
      const [usersResponse, amberAlertsResponse, redAlertsResponse] = await Promise.all([
        this.api.user.loadActiveUsers(this.userQueryParams, LOCATION_API_REFRESH_INTERVAL)(),
        this.api.alarms.loadAmberAlarms({})(),
        this.api.alarms.loadAlarms({})(),
      ]);

      runInAction(() => {
        this.removeData();
        this.users = usersResponse.data.map((activity) => new ActiveUser(this, activity));
        this.alarms = [...amberAlertsResponse.data.results, ...redAlertsResponse.data.results];

        this.isReady = true;
      });
    } catch (e) {
      Logger.error('Invalid load Activity Map API response', e);
    }
  }

  @action.bound
  removeUsers(): void {
    this.users.forEach((user) => eventBus.unregister(user));
    this.users = [];
  }

  @action.bound
  removeAlarms(): void {
    this.alarms.forEach((alarm) => eventBus.unregister(alarm));
    this.alarms = [];
  }

  @action.bound
  changeSearchQuery(query: unknown): void {
    this.searchQuery = query as string;
    this.isReady = false;
    this.bounds = '';
    this.locationDetails.closeDetails();

    this.startRefreshingMap();
  }

  @action.bound
  changeBounds(bounds: string): void {
    this.bounds = bounds;

    this.startRefreshingMap();
  }

  @subscribe(EventType.DeletedUsers)
  @action
  deletedUsers({ payload }: ActionEvent<DeletedUsersPayload>): void {
    if (!this.isReady) {
      return;
    }

    this.users = this.users.filter((user) => {
      if (payload.users.includes(user.uuid)) {
        eventBus.unregister(user);
        return false;
      }
      return true;
    });
  }

  @action.bound
  startRefreshingMap(): void {
    this.stopRefreshingMap();
    this.refreshMapInterval = setInstantInterval(this.refreshActiveUsers, LOCATION_API_REFRESH_INTERVAL)();
  }

  @action.bound
  stopRefreshingMap(): void {
    clearInterval(this.refreshMapInterval);
  }

  removeData(): void {
    this.removeUsers();
    this.removeAlarms();
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.removeData();
    this.locationDetails.closeDetails();
    this.isReady = false;
    this.searchQuery = '';
    this.bounds = '';
  }

  @computed get userQueryParams(): ActivityMapQueryParams {
    return {
      query: this.searchQuery,
      bounds: this.bounds,
    };
  }

  incidentQueryParams(type: IncidentType[]): ActivityMapQueryParams {
    return {
      query: this.searchQuery,
      type: type.join(','),
      status: IncidentStatus.Open,
      bounds: this.bounds,
    };
  }
}
